import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { Language } from '@hmax-order/shared/enums';
import {
  FIREBASE_AUTH,
  clearStoredPreferences,
  readStoredLanguage,
  readStoredScheme,
  storeLanguage,
  storeScheme,
} from '@hmax-order/frontend/utils';
import { UserStateModel, UserStore } from '@hmax-order/frontend/state';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { TranslocoService } from '@jsverse/transloco';
import { AuthUtils } from './auth.utils';
import { UserService } from '../user/user.service';
import { environment } from '../../../environments/environment';
import { catchError, defer, from, map, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated = false;
  private _urlBase = environment.apiUrl + '/auth/';
  private _urlSubscriptionCompanyInfo = environment.apiUrl + '/subscription-info';
  private _urlLogin = this._urlBase + 'login';
  private _urlLoginGoogle = this._urlBase + 'login-google';
  private _urlLoginWithToken = this._urlBase + 'login-by-token';
  private _urlForgotPassword = this._urlBase + 'forgot-password';
  private _urlResetPassword = this._urlBase + 'reset-password';
  private _urlLogout = this._urlBase + 'logout';
  private _urlUnlockSession = this._urlBase + 'unlock-session';
  private _httpClient = inject(HttpClient);
  private _userService = inject(UserService);
  private _fuseConfigService = inject(FuseConfigService);
  private _translocoService = inject(TranslocoService);
  private _userStore = inject(UserStore);
  private _injector = inject(Injector);

  /**
   * Firebase resolvido SOB DEMANDA, não no campo.
   *
   * O `provideAuth()` instancia este serviço no bootstrap de TODA rota
   * (`provideEnvironmentInitializer`), então injetar no campo inicializava o Firebase Auth
   * para todo mundo — inclusive o hóspede anônimo do cardápio, que nunca autentica. Isso
   * custa a criação do iframe de auth e uma ida a servidores do Google (`getProjectConfig`),
   * que numa rede ruim segura o carregamento por vários segundos antes de a tela aparecer.
   *
   * O Firebase só é usado no login com Google e no logout, então o custo agora é pago por
   * quem realmente entra na plataforma.
   */
  private get _firebaseAuth() {
    return this._injector.get(FIREBASE_AUTH);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setters & getters for access token
   */
  set accessToken(token: string) {
    // Só atualizar o token se houver outro
    if (token) {
      localStorage.setItem('accessToken', token);
    }
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  /**
   * Verificar se houve alteração no tema e atualizar
   * @param darkMode Status do darkMode
   */
  private checkChangeTheme(darkMode: boolean, options?: { persistAsDefault?: boolean }) {
    // 🔴 O `darkMode` do token é o PADRÃO do usuário, e preferência escolhida no aparelho vence.
    // Sem esta guarda, o F5 restaura a sessão por `signInUsingToken`, isto roda de novo e o valor
    // do perfil desfaz o tema que o usuário acabou de escolher no botão. A tela de preferências
    // grava no servidor E no aparelho, então mudança feita lá continua valendo nas duas pontas.
    if (readStoredScheme()) {
      return;
    }

    // `config` do Fuse é setter sem getter: esta leitura devolve `undefined` sempre, então a
    // comparação abaixo nunca evita a escrita. Mantida como estava para não alterar o efeito.
    const newScheme = darkMode ? 'dark' : 'light';
    const oldScheme = this._fuseConfigService.config?.scheme;

    if (oldScheme !== newScheme) {
      this._fuseConfigService.config = { scheme: newScheme };
    }

    // 🔴 Grava o valor do TOKEN na primeira vez, e é isto que tira o flash de tema claro de quem
    // nunca tocou no botão: sem gravar, o bootstrap seguinte cai no default `light` e só corrige
    // quando a resposta da sessão chega. `persistAsDefault` é ligado só pelo caminho do token — o
    // `checkChangeTheme(false)` do logout não pode gravar 'light', senão o padrão do próximo
    // usuário a entrar neste aparelho nunca se aplicaria.
    if (options?.persistAsDefault) {
      storeScheme(newScheme);
    }
  }

  /**
   * Verificar se houve alteração no idioma selecionado e atualizar
   * @param language Idioma
   */
  private checkLanguage(language?: Language): void {
    const exists = Object.values(Language).includes(language);

    if (!exists) {
      return;
    }

    // Mesma regra do tema: idioma escolhido no aparelho vence o padrão que vem no token.
    if (readStoredLanguage(Object.values(Language))) {
      return;
    }

    const currencyLanguage = this._translocoService.getActiveLang();

    if (language && currencyLanguage !== language) {
      this._translocoService.setActiveLang(language);
    }

    // Grava o idioma do token na primeira vez, pelo mesmo motivo do tema: sem isto o bootstrap
    // seguinte resolve pelo navegador e a primeira pintura sai no idioma errado.
    storeLanguage(language);
  }

  // ----------------------------------------------------------------------------------------------------
  // Public methods
  // ----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post(this._urlForgotPassword, { email });
  }

  /**
   * Redefinir senha
   */
  resetPassword(token: string, password: string): Observable<any> {
    return this._httpClient.post(this._urlResetPassword, { token, password });
  }

  /**
   * Atualizar access token armazenado localmente
   * e definir usuário no serviço de usuário
   */
  setToken(response: { accessToken: string; user: UserStateModel }): boolean {
    // Resposta inválida — o `false` que o `catchError` de rede injeta, ou um
    // payload sem token/usuário — NÃO pode marcar a sessão como autenticada nem
    // limpar o usuário atual. Falhar aqui é o que impede o estado "meio-logado":
    // `_authenticated = true` com usuário vazio e o token velho ainda no storage.
    if (!response?.accessToken || !response?.user) {
      return false;
    }
    const { accessToken, user } = response;
    this._authenticated = true;
    this.accessToken = accessToken;
    this._userService.user = user;
    this.checkChangeTheme(user?.darkMode, { persistAsDefault: true });
    this.checkLanguage(user?.language);
    return true;
  }

  /**
   * Sign in
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this._httpClient.post(this._urlLogin, credentials).pipe(
      // Resposta 200 porém inválida (sem token/usuário) não pode navegar como
      // sucesso: `setToken` recusa e devolve `false`, então propagamos erro para
      // o componente exibir o alerta em vez de rebater silenciosamente no guard.
      switchMap((response: { accessToken: string; user: UserStateModel }) =>
        this.setToken(response) ? of(response) : throwError(() => new Error('INVALID_LOGIN_RESPONSE')),
      ),
    );
  }

  /**
   * Autenticar com a conta Google a partir do ID Token obtido via Firebase Auth.
   * O token é validado no backend, que retorna o JWT padrão da HMAX.
   * @param idToken ID Token gerado pelo Firebase Auth
   */
  signInWithGoogle(idToken: string): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this.requestGoogleLogin(idToken);
  }

  /**
   * Trocar um ID Token do Google por um JWT da HMAX no backend e persistir a sessão.
   * Reutilizado pelo login manual com Google e pela renovação silenciosa de sessão.
   * @param idToken ID Token gerado pelo Firebase Auth
   */
  private requestGoogleLogin(idToken: string): Observable<{ accessToken: string; user: UserStateModel }> {
    return this._httpClient.post<{ accessToken: string; user: UserStateModel }>(this._urlLoginGoogle, { idToken }).pipe(
      // Mesmo endurecimento do defeito 2, aplicado ao caminho gêmeo: esta é a
      // reauth silenciosa (interceptor no 401, `check()` em token expirado). Sem
      // recusar o 200 inválido, `tryGoogleSilentReauth` emitiria `true` com o
      // token velho e o app seguiria "autenticado" sem sessão válida.
      switchMap((response) => (this.setToken(response) ? of(response) : throwError(() => new Error('INVALID_LOGIN_RESPONSE')))),
    );
  }

  /**
   * Tentar renovar a sessão de forma silenciosa reaproveitando a conta Google já
   * autenticada no Firebase. Gera um novo ID Token e o troca por um novo JWT da HMAX,
   * evitando que o usuário precise logar novamente quando o token da HMAX expira.
   * @returns Observable<boolean> indicando se a renovação foi bem sucedida
   */
  tryGoogleSilentReauth(): Observable<boolean> {
    /**
     * 🔴 Esperar o `authStateReady()` é o que faz esta renovação existir no CARREGAMENTO da página.
     * O `setPersistence(browserLocalPersistence)` do `firebase.provider.ts` restaura a sessão de
     * forma ASSÍNCRONA: ler `currentUser` de imediato devolve `null` durante o bootstrap, então o
     * `return of(false)` abaixo disparava sempre e o único anteparo contra o logout duro no 401
     * nunca rodava — justamente na janela em que ele é necessário.
     */
    return defer(() => this._firebaseAuth.authStateReady()).pipe(
      switchMap(() => {
        const currentUser = this._firebaseAuth.currentUser;

        // Sem sessão Google ativa (ex.: login por e-mail/senha): nada a renovar
        if (!currentUser) {
          return of(false);
        }

        // Força a renovação do ID Token junto ao Firebase antes de trocá-lo pelo JWT
        return from(currentUser.getIdToken(true)).pipe(
          switchMap((idToken) => this.requestGoogleLogin(idToken)),
          map(() => true),
        );
      }),
      catchError(() => of(false)),
    );
  }

  /**
   * Sign in using the existent access token
   */
  signInUsingToken(): Observable<any> {
    // Sign in using the token
    return this._httpClient.post(this._urlLoginWithToken, { accessToken: this.accessToken }).pipe(
      catchError(() => of(false)),
      // Falha de rede vira `false` acima. `setToken` recusa resposta inválida e
      // devolve `false`, então o guard recebe "não autenticado" em vez de um
      // sucesso falso que limparia o usuário e manteria o token velho.
      switchMap((response: any) => of(this.setToken(response))),
    );
  }

  /**
   * Sign out
   */
  /**
   * @param options `userInitiated` marca a saída DELIBERADA (o usuário clicou em sair). Só ela
   * apaga a preferência de idioma e tema.
   */
  signOut(options?: { userInitiated?: boolean }): Observable<any> {
    if (this.accessToken) {
      // Não pode fazer esta requisição sem token, pois não seria seguro e ficaria em loop devido
      // ao Auth.interceptor chamar o SignOut quando ocorre um Unauthorized
      this._httpClient.post(this._urlLogout, null).subscribe();
    }
    // Encerrar também a sessão do Firebase para impedir que a renovação silenciosa
    // volte a logar um usuário que pediu para sair
    firebaseSignOut(this._firebaseAuth).catch(() => undefined);
    this.cleanLocalStorageAndState();

    /**
     * 🔴 A preferência de idioma e tema sai APENAS na saída deliberada. Ela é do usuário, não do
     * navegador, e sem apagar o próximo a entrar neste aparelho herdaria a escolha do anterior.
     *
     * ⚠️ Mas apagar em TODO `signOut` anulava o objetivo da persistência: o interceptor chama este
     * método no 401 de token expirado e no 403 de fora de horário, então uma sessão que só venceu
     * levava embora o idioma e o tema escolhidos — exatamente o que a mudança queria preservar.
     */
    if (options?.userInitiated) {
      clearStoredPreferences();
    }

    this.checkChangeTheme(false);
    this._authenticated = false;
    return of(true);
  }

  /**
   * Limpar apenas os dados necessários do local storage e state
   */
  cleanLocalStorageAndState() {
    localStorage.removeItem('accessToken');
    // Zerar a sessão basta: os recursos por empresa dependem do signal
    // `currentCompanyId`, então vão sozinhos para ocioso e descartam o valor.
    this._userStore.setUser(null);
  }

  /**
   * Sign up
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
    // return this._httpClient.post(this._urlLogout, user);
    return of(true);
  }

  /**
   * Unlock session
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post(this._urlUnlockSession, credentials);
  }

  /**
   * Verificar status da autenticação
   */
  check(): Observable<boolean> {
    // Verifica se o usuário está logado
    if (this._authenticated) {
      return of(true);
    }

    // Sem token ou token expirado: tenta renovar silenciosamente reaproveitando a
    // sessão Google ainda ativa no Firebase. Só desconecta se a renovação falhar.
    if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken)) {
      return this.tryGoogleSilentReauth();
    }

    // Se o access token existe e não está expirado, faz login com ele
    return this.signInUsingToken();
  }
}
